import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  Card,
  Flex,
  Pagination,
  Table,
  Text,
  TextInput,
  ActionIcon,
  Select,
  LoadingOverlay,
  UnstyledButton,
} from "@mantine/core";
import { Link } from "react-router-dom";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";

const MIN_LOADING_TIME = 500;

export default function AffiliateUserList({ reqData, refresh }) {
  const [loading, setLoading] = useState(true);
  const loadingStartTime = useRef(null);
  const [pageCount, setPageCount] = useState(-1);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [filters, setFilters] = useState({
    scope: "entire",
  });
  const [init, setInit] = useState(false);
  const CancelToken = axios.CancelToken;
  const cancelRef = useRef(null);

  const columns = [
    {
      header: "Name",
      id: "id",
      enableSorting: false,
      cell: (info) => {
        const { original } = info.row;
        return (
          <UnstyledButton
            component={Link}
            to={`/users/${original.user_id}`}
            target="_blank"
          >
            <Text size="sm" td="underline">
              {original.first_name} {original.last_name}
            </Text>
          </UnstyledButton>
        );
      },
    },
    {
      header: "Email",
      accessorKey: "email",
      id: "email",
      enableSorting: false,
      cell: (info) => info.getValue(),
    },
    {
      header: "Phone",
      accessorKey: "mobile_phone",
      id: "phone",
      enableSorting: false,
      cell: (info) => info.getValue(),
    },
  ];

  const table = useReactTable({
    data: items,
    columns,
    manualPagination: true,
    enableMultiSort: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  });

  const setLoadingWithMinimum = (isLoading) => {
    if (isLoading) {
      loadingStartTime.current = Date.now();
      setLoading(true);
    } else {
      const elapsedTime = Date.now() - loadingStartTime.current;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

      if (remainingTime > 0) {
        setTimeout(() => {
          setLoading(false);
        }, remainingTime);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!init) return;
    let p = new Promise((resolve) => {
      resolve(table.setPageIndex(0));
    });
    p.then(() => {
      fetchData();
    });
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    table.setPageIndex(0);
    if (table.getState().pagination.pageIndex === 0) {
      fetchData();
    }
  }, [searchValue]);

  useEffect(() => {
    if (!init) return;
    fetchData();
  }, [JSON.stringify(table.getState().pagination), refresh]);

  function onSearchSubmit() {
    table.setPageIndex(0);
    fetchData();
  }

  function fetchData() {
    setLoadingWithMinimum(true);

    const cancel = cancelRef.current;
    if (cancel) {
      cancel();
    }

    const req = {
      ...reqData,
      page_size: table.getState().pagination.pageSize,
      page:
        table.getState().pagination.pageIndex === -1
          ? 0
          : table.getState().pagination.pageIndex,
      search_value: searchValue,
      ...filters,
    };

    axios
      .post(`/affiliates/${reqData.affiliate_id}/retrieve-users/`, req, {
        cancelToken: new CancelToken(function executor(c) {
          cancelRef.current = c;
        }),
      })
      .then(({ data }) => {
        setItems(data.response[0].data);
        setTotal(data.response[0].total);
        setPageCount(data.response[0].page_count);
        setInit(true);
        setLoadingWithMinimum(false);
        window.scrollTo(0, 0);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          // setItems([]);
          // setTotal(0);
          // setPageCount(1);
          setInit(true);
          setLoadingWithMinimum(false);
        }
      });
  }

  return (
    <div>
      <Flex style={{ width: "100%" }} gap="xs" align="center">
        <TextInput
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search..."
          size="md"
          style={{
            flexGrow: 1,
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onSearchSubmit();
            }
          }}
          rightSection={
            searchValue && (
              <ActionIcon
                variant="light"
                radius="xl"
                color="gray"
                size="xs"
                onClick={() => {
                  setSearchValue("");
                  onSearchSubmit();
                }}
              >
                ×
              </ActionIcon>
            )
          }
        />
      </Flex>
      <Card mt="sm" style={{ position: "relative" }} mih={250}>
        <LoadingOverlay visible={loading} />
        <Table.ScrollContainer minWidth={1000}>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                {table.getFlatHeaders().map((header) => (
                  <Table.Th key={header.id}>
                    {header.column.columnDef.header}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {!loading && table.getRowModel().rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={table.getFlatHeaders().length}>
                    No results
                  </Table.Td>
                </Table.Tr>
              )}
              {table.getRowModel().rows.map((row, index) => {
                return (
                  <Table.Tr key={index}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <Table.Td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
      <Flex mt="lg" gap="xs" align="center">
        <Pagination
          value={table.getState().pagination.pageIndex + 1}
          onChange={(e) => {
            table.setPageIndex(e - 1);
          }}
          total={pageCount}
        />
        <Select
          size="sm"
          style={{ width: "110px" }}
          value={`${table.getState().pagination.pageSize}`}
          onChange={(e) => table.setPageSize(parseInt(e))}
          data={[5, 10, 20, 30, 40, 50].map((pageSize) => ({
            label: `${pageSize} items`,
            value: `${pageSize}`,
            placeholder: "Select a pagesize",
          }))}
        />
        <Text size="sm">
          {total} total item{total === 1 ? "" : "s"}
        </Text>
      </Flex>
    </div>
  );
}
